import { AxiosInstance } from 'axios'
import { PaginatedResult } from 'types/utility'
import { Group, Team, TeamPayload, UpdateTeamPayload } from 'types/groups'
import { TEAMS_ENDPOINTS } from 'api/ApiRoutes'
import { SummaryData } from 'types/misc'
import { formatDateParams } from 'utils/Functions'
import { DateRangeFilterInAPI } from 'types/reports'
import { MemberSummaryRow } from 'types/members'

class TeamsApi {
  constructor(private axiosInstance: AxiosInstance) {}

  getTeams = async ({
    page_size,
    page,
    q,
    team_token_id,
    include_parent = false
  }: {
    page_size?: number
    q?: string
    page?: number | string
    team_token_id?: string
    include_parent?: boolean
  }) => {
    const d = await this.axiosInstance.get<PaginatedResult<Team>>(
      TEAMS_ENDPOINTS.BASE,
      {
        params: { q, page_size, page, team_token_id, include_parent }
      }
    )
    return d.data
  }

  getTeam = async ({ teamId }: { teamId: Team['token_id'] }) => {
    const { data } = await this.axiosInstance.get<Group>(
      TEAMS_ENDPOINTS.TEAM(teamId)
    )
    return data
  }

  updateTeam = async (teamId: Group['token_id'], team: UpdateTeamPayload) => {
    const { data } = await this.axiosInstance.put(
      TEAMS_ENDPOINTS.TEAM(teamId),
      { team }
    )
    return data
  }

  createTeam = async (team: TeamPayload, isTeamBody = true) => {
    const { data } = await this.axiosInstance.post<Group>(
      TEAMS_ENDPOINTS.BASE,
      isTeamBody ? { team } : team
    )
    return data
  }

  deleteTeam = async ({ teamId }: { teamId: Team['token_id'] }) => {
    const d = await this.axiosInstance.delete(TEAMS_ENDPOINTS.TEAM(teamId))
    return d.data
  }

  getTeamSummary = async ({
    teamId,
    dates
  }: {
    teamId: Team['token_id']
    dates: string[]
  }) => {
    const d = await this.axiosInstance.get<SummaryData>(
      TEAMS_ENDPOINTS.TEAM_TOTALS(teamId),
      {
        params: formatDateParams<DateRangeFilterInAPI>(dates)
      }
    )
    return d.data
  }

  getMembersSummary = async ({
    teamId,
    start_date,
    end_date,
    page,
    page_size
  }: {
    teamId: Team['token_id']
    start_date: string
    end_date: string
    page: string | number
    page_size: string | number
  }) => {
    const d = await this.axiosInstance.get<PaginatedResult<MemberSummaryRow>>(
      TEAMS_ENDPOINTS.TEAM_MEMBER_SUMMARY(teamId),
      {
        params: {
          end_date,
          start_date,
          page,
          limit: page_size
        }
      }
    )
    return d.data
  }
}

export default TeamsApi
